"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import ContactForm from "./ContactForm";
import InquiryPills from "./InquiryPills";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

export default function ContactPageContent() {
  const searchParams = useSearchParams();
  const [initialTopic, setInitialTopic] = useState<string>("");

  useEffect(() => {
    const topicParam = searchParams.get("topic");
    if (topicParam) {
      setInitialTopic(topicParam);
    }
  }, [searchParams]);

  const handleSelectTopic = (topic: string) => {
    setInitialTopic(topic);
    // Scroll to form
    const formElement = document.getElementById("contact-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Page Header */}
      <section className="py-24 px-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-neutral-400 mb-4">
            <a
              href="mailto:sales@e2eint.com"
              className="text-blue-400 hover:underline"
            >
              sales@e2eint.com
            </a>
          </p>
          <p className="text-neutral-500">We respond within 24 hours.</p>
        </div>
      </section>

      {/* Section 1: Contact Form */}
      <section id="contact-form" className="py-24 px-6">
        <ContactForm initialTopic={initialTopic} />
      </section>

      {/* Section 2: Request Demo Block */}
      <section className="py-12 px-6">
        <div className="max-w-2xl mx-auto glass-card rounded-2xl p-8 border-emerald-500/30 shadow-emerald-500/10">
          <div className="flex items-center gap-4 mb-6">
            <Icon name="PlayCircleIcon" size={48} className="text-emerald-400" />
            <h2 className="text-2xl font-semibold text-white">
              Request a Demo
            </h2>
          </div>
          <p className="text-neutral-300 mb-4">
            We can demo using your Sandbox System connectivity (SAP, Oracle,
            NetSuite, etc.) to prove flow, mapping, and controls.
          </p>
          <p className="text-neutral-500 text-sm">
            Select &quot;Demo Request&quot; in the topic dropdown above to provide
            demo details.
          </p>
        </div>
      </section>

      {/* Section 3: Inquiry Category Pills */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-center text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-6">
            Quick Topic Selection
          </h3>
          <InquiryPills onSelectTopic={handleSelectTopic} />
        </div>
      </section>

      {/* Section 4: Ethics Note */}
      <section className="py-12 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <Icon
            name="ShieldCheckIcon"
            size={48}
            className="text-blue-400 mx-auto mb-4"
          />
          <p className="text-lg text-neutral-300">
            We don&apos;t sell middleware licenses. We design and deliver
            integrations.
          </p>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prefer to talk first?
          </h2>
          <Link
            href="#schedule"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/10 text-white rounded-full text-lg font-medium hover:bg-white/10 transition-colors"
          >
            Schedule a call
            <Icon name="PhoneIcon" size={20} />
          </Link>
        </div>
      </section>
    </>
  );
}